import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { adminService } from '../services/adminService';
import { AdminSession, AdminSessionsFilter } from '../types/admin';
import { PAGINATION } from '../config/defaults';

export interface UseAdminSessionsOptions {
  initialLimit?: number;
  autoLoad?: boolean;
}

export interface UseAdminSessionsReturn {
  sessions: AdminSession[];
  allSessions: AdminSession[]; // All loaded sessions (unfiltered)
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  filter: AdminSessionsFilter;
  
  // Actions
  loadSessions: (resetData?: boolean) => Promise<void>;
  loadMore: () => Promise<void>;
  setFilter: (newFilter: Partial<AdminSessionsFilter>) => void;
  refresh: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for managing admin sessions data with LOCAL filtering and backend pagination
 */
export function useAdminSessions({
  initialLimit = PAGINATION.ADMIN_SESSIONS_DEFAULT_LIMIT,
  autoLoad = true
}: UseAdminSessionsOptions = {}): UseAdminSessionsReturn {
  const [allSessions, setAllSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  
  const [filter, setFilterState] = useState<AdminSessionsFilter>({
    limit: initialLimit,
    userIdFilter: '',
    titleFilter: '',
    sessionType: undefined
  });

  // Use ref to track if component is mounted and loading state
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Apply local filtering to sessions - NO BACKEND CALLS
  const sessions = useMemo(() => {
    let filteredSessions = [...allSessions];
    
    // Apply titleFilter (main search - searches both title AND userId)
    if (filter.titleFilter) {
      const searchLower = filter.titleFilter.toLowerCase();
      filteredSessions = filteredSessions.filter((session: AdminSession) => {
        const titleLower = session.title.toLowerCase();
        const userIdLower = session.userId.toLowerCase();
        return titleLower.includes(searchLower) || userIdLower.includes(searchLower);
      });
    }
    
    // Apply userIdFilter (advanced filter - ONLY applies if titleFilter is empty)
    if (filter.userIdFilter && !filter.titleFilter) {
      const userIdLower = filter.userIdFilter.toLowerCase();
      filteredSessions = filteredSessions.filter((session: AdminSession) => 
        session.userId.toLowerCase().includes(userIdLower)
      );
    }
    
    // Apply sessionType filter
    if (filter.sessionType) {
      filteredSessions = filteredSessions.filter((session: AdminSession) => 
        session.sessionType === filter.sessionType
      );
    }
    
    // Sort by updatedAt (most recent first)
    return filteredSessions.sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [allSessions, filter.userIdFilter, filter.titleFilter, filter.sessionType]);

  /**
   * Load sessions from backend (for initial load and pagination only)
   * NO filtering sent to backend - we filter locally
   */
  const loadSessions = useCallback(async (resetData: boolean = false) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      // For backend API call, ONLY send pagination params - NO filtering
      const backendFilter: AdminSessionsFilter = resetData 
        ? { limit: filter.limit } 
        : { 
            limit: filter.limit, 
            continuationToken: continuationToken || undefined,
            offset: continuationToken ? undefined : nextOffset || allSessions.length
          };

      const response = await adminService.getAllSessions(backendFilter);

      // Always update state - don't rely on isMountedRef in StrictMode
      if (resetData) {
        setAllSessions(response.sessions || []);
        setTotalCount(response.sessions?.length || 0);
      } else {
        setAllSessions(prev => [...prev, ...(response.sessions || [])]);
        setTotalCount(prev => prev + (response.sessions?.length || 0));
      }

      setContinuationToken(response.continuationToken || null);
      setNextOffset(response.nextOffset ?? null);
      setHasMore(response.hasMore || false);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load admin sessions';
      console.error('Failed to load admin sessions:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [filter.limit, continuationToken, nextOffset, allSessions.length]);

  /**
   * Load more sessions (pagination)
   */
  const loadMore = useCallback(async () => {
    if (loading || !hasMore || isLoadingRef.current) {
      return;
    }
    
    // Check if we can paginate - need either continuation token or next offset
    const canPaginate = continuationToken || nextOffset !== null;
    if (!canPaginate) {
      return;
    }
    
    await loadSessions(false);
  }, [loading, hasMore, continuationToken, nextOffset, loadSessions]);

  /**
   * Update filter (LOCAL ONLY - no backend calls)
   */
  const setFilter = useCallback((newFilter: Partial<AdminSessionsFilter>) => {
    setFilterState(prevFilter => ({ ...prevFilter, ...newFilter }));
    // Note: No longer reset pagination or clear sessions - filtering is now local
  }, []);

  /**
   * Refresh sessions (reload from start)
   */
  const refresh = useCallback(async () => {
    console.log('[useAdminSessions] refresh called - reloading from start');
    setContinuationToken(null);
    setNextOffset(null);
    setAllSessions([]);
    setTotalCount(0);
    await loadSessions(true);
  }, [loadSessions]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-load sessions on mount only (no longer reload on filter changes)
  useEffect(() => {
    if (autoLoad && !isLoadingRef.current && allSessions.length === 0) {
      loadSessions(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad]);

  return {
    sessions,
    allSessions,
    loading,
    error,
    hasMore,
    totalCount,
    filter,
    loadSessions,
    loadMore,
    setFilter,
    refresh,
    clearError
  };
}